




import Vue, { PropType } from "vue";
import { Chart } from "chart.js";
import { Data, Methods, Computed, Props } from "./types";
import { BME_PART, ApiResponse } from "@/api/types";
// @ts-ignore
import { customDraw } from "./roundedBar";
import dayjs from "dayjs";
import {
  grayscale70,
  primaryBlueDefault,
  grayscale40,
  colorCodeToRGBA
} from "@/assets/styles/vars";

const STUDENT_TREND_CATEGORY_LABELS = {
  MEMBER_REGISTRATION_COUNT: "会員登録数",
  EVENT_ENTRY_COUNT: "エントリー数",
  OFFER_RESULT_COUNT: "内定数"
};

// TIMELINEの長さ 17ヶ月 * 3(上中下旬)
const TIMELINE_LENGTH = 17 * 3;

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "StudentTrendChart",
  props: {
    graduateYear: {
      type: Number as PropType<Props["graduateYear"]>,
      required: true
    },
    studentJobHuntingTrendCollection: {
      type: Object as PropType<Props["studentJobHuntingTrendCollection"]>,
      required: true
    },
    distinctUniversities: {
      type: Array as PropType<Props["distinctUniversities"]>,
      required: true
    },
    selectedStudentTrendCategory: {
      type: String as PropType<Props["selectedStudentTrendCategory"]>,
      required: true
    },
    selectedGraduateYears: {
      type: Array as PropType<Props["selectedGraduateYears"]>,
      required: true
    }
  },
  data() {
    return {
      bindTo: "chart",
      chart: null,
      startAt: dayjs(`${this.graduateYear - 2}-04-01`),
      now: dayjs(),
      chartData: {
        labels: Array(TIMELINE_LENGTH),
        datasets: [
          {
            type: "line",
            yAxisID: "count-by-category",
            borderWidth: 1,
            borderColor: grayscale40,
            backgroundColor: grayscale40,
            fill: "start",
            lineTension: 0,
            data: []
          },
          {
            type: "line",
            yAxisID: "count-by-category",
            borderWidth: 1,
            borderColor: grayscale70,
            backgroundColor: grayscale70,
            fill: "start",
            lineTension: 0,
            data: []
          },
          {
            type: "line",
            yAxisID: "count-by-category",
            borderWidth: 1,
            borderColor: primaryBlueDefault,
            backgroundColor: primaryBlueDefault,
            fill: "start",
            lineTension: 0,
            data: []
          }
        ]
      },
      chartOptions: {
        hover: {
          onHover: (_event, activeElements) => {
            // @ts-ignore
            this.onMousemoveChart(activeElements[0]);
          }
        },
        maintainAspectRatio: false,
        cornerRadius: 5,
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              offset: true,
              ticks: {
                display: false
              },
              gridLines: {
                display: false
              }
            }
          ],
          yAxes: [
            {
              id: "count-by-category",
              ticks: {
                display: false
              },
              gridLines: {
                display: false
              }
            }
          ]
        },
        layout: {
          padding: {
            // NOTE バーチャートが右にずれているため
            left: -80,
            // NOTE EventTimeLineの右端の端数（20px）ずらしている
            right: 20,
            top: 10,
            bottom: 0
          }
        },
        elements: {
          point: {
            // NOTE Lineチャートのポイントを消す
            radius: 0,
            // NOTE ホバー認識エリアを広げる
            hitRadius: 10
          }
        },
        tooltips: {
          callbacks: {
            // NOTE タイトルは不要のためデフォルト設定を上書き
            title: () => "",
            label: (tooltipItem, data): string => {
              if (!data.datasets || tooltipItem.datasetIndex === undefined) {
                return "";
              }
              // NOTE デフォルトでは数値が表示されるためラベルのみを返す
              const label = data.datasets[tooltipItem.datasetIndex].label;
              if (label) {
                return label;
              } else {
                return "";
              }
            }
          }
        }
      },
      lastMouseover: { _datasetIndex: null, _index: null }
    };
  },
  computed: {
    // UniversitySelctBoxと共通化
    universityName() {
      if (
        !this.distinctUniversities ||
        !this.distinctUniversities.length ||
        !this.studentJobHuntingTrendCollection
      ) {
        return "";
      }
      const selectedUniversity = this.distinctUniversities.find(
        e => e.id === this.studentJobHuntingTrendCollection!.university_id
      );
      if (!selectedUniversity || !selectedUniversity.name) return "";
      return selectedUniversity.name;
    }
  },
  watch: {
    studentJobHuntingTrendCollection: {
      deep: true,
      immediate: true,
      handler() {
        this.onChangestudentJobHuntingTrendCollection();
        this.onChangeChartDatasets();
      }
    },
    selectedStudentTrendCategory: {
      deep: true,
      immediate: true,
      handler() {
        this.onChangestudentJobHuntingTrendCollection();
        this.onChangeChartDatasets();
      }
    },
    selectedGraduateYears() {
      this.onChangeChartDatasets();
    },
    "chartData.datasets": {
      immediate: true,
      handler() {
        this.onChangeChartData();
      }
    },
    "chartData.labels"() {
      this.onChangeChartData();
    }
  },
  created() {
    this.customDrawInit();
  },
  mounted() {
    this.generateChart();
    this.onChangeChartDatasets();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    generateChart() {
      if (this.chart) this.chart.destroy();
      const canvas = document.getElementById(this.bindTo) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");

      if (!canvas || !ctx || !this.chartData || !this.chartData.datasets)
        return;

      this.chartData.datasets.map(dataset => {
        dataset.backgroundColor = this.generateGradient(
          ctx,
          dataset.backgroundColor as string
        );
      });

      this.chart = new Chart(ctx, {
        data: this.chartData,
        options: this.chartOptions
      });
    },
    generateGradient(ctx, bgColor) {
      const gradient = ctx.createLinearGradient(0, 0, 0, 100);
      // NOTE 年度ごとに設定した背景色をもとにグラデーションを生成
      gradient.addColorStop(0, colorCodeToRGBA(bgColor, 0.3));
      gradient.addColorStop(0.3, colorCodeToRGBA(bgColor, 0.1));
      gradient.addColorStop(1, colorCodeToRGBA(bgColor, 0));
      return gradient;
    },
    customDrawInit() {
      // @ts-ignore
      Chart.elements.Rectangle.prototype.draw = customDraw;
    },
    onChangestudentJobHuntingTrendCollection() {
      if (
        !this.selectedStudentTrendCategory ||
        !this.studentJobHuntingTrendCollection
      )
        return;
      const studentJobHuntingTrendDataset = this.studentJobHuntingTrendCollection.datasets?.find(
        e => e.category === this.selectedStudentTrendCategory
      );
      if (!studentJobHuntingTrendDataset) return;
      const newData = { ...this.chartData };
      newData.datasets![0].data = studentJobHuntingTrendDataset
        ? this.generateData(
            studentJobHuntingTrendDataset.data,
            this.graduateYear - 2
          ).map(e => e.count)
        : [];
      newData.datasets![1].data = studentJobHuntingTrendDataset
        ? this.generateData(
            studentJobHuntingTrendDataset.data,
            this.graduateYear - 1
          ).map(e => e.count)
        : [];
      newData.datasets![2].data = studentJobHuntingTrendDataset
        ? this.generateData(
            studentJobHuntingTrendDataset.data,
            this.graduateYear
          ).map(e => e.count)
        : [];

      this.chartData = newData;

      this.$emit("chart:update");
    },
    onChangeChartData() {
      if (!this.chart) return;
      this.chart.update();
      this.$emit("chart:update");
    },
    onChangeChartDatasets() {
      if (!this.chart || !this.chartData || !this.chartData.datasets) return;
      this.chartData.datasets.map((dataset, i) => {
        if (i === 0) {
          // 卒年度 - 2年のデータ
          dataset.hidden = !this.selectedGraduateYears.includes(
            this.graduateYear - 2
          );
          dataset.label = `${
            STUDENT_TREND_CATEGORY_LABELS[this.selectedStudentTrendCategory]
          }(${this.graduateYear - 2}卒)`;
        } else if (i === 1) {
          // 卒年度 - 1年のデータ
          dataset.hidden = !this.selectedGraduateYears.includes(
            this.graduateYear - 1
          );
          dataset.label = `${
            STUDENT_TREND_CATEGORY_LABELS[this.selectedStudentTrendCategory]
          }(${this.graduateYear - 1}卒)`;
        } else if (i === 2) {
          // 卒年度のデータ
          dataset.hidden = !this.selectedGraduateYears.includes(
            this.graduateYear
          );
          dataset.label = `${
            STUDENT_TREND_CATEGORY_LABELS[this.selectedStudentTrendCategory]
          }(${this.graduateYear}卒)`;
        }
      });
      this.chart.update();
      this.$emit("chart:update");
    },
    generateData(data, graduateYear) {
      const filteredData = data.filter(e => {
        // NOTE 引数のgraduateYear(プランのとは別物)で絞り込みgraduateYear-2 ~ graduateYear-1、Partが0(不明)の場合は他社同様に表示対象から除外する
        if (
          graduateYear === e.graduate_year &&
          (graduateYear - 2 === e.year || graduateYear - 1 === e.year) &&
          e.part !== 0
        ) {
          // NOTE graduateYear - 2 の場合 monthが4以上のものを取得する
          if (e.year === graduateYear - 2) return e.month >= 4;
          // NOTE graduateYear - 1 の場合 monthが8以下のものを取得する
          else if (e.year === graduateYear - 1) return e.month <= 8;
          else return false;
        } else {
          return false;
        }
      });
      // NOTE 期間内対象データがない場合は不要なラインの描画を避けるため空の配列を返す
      if (filteredData.length === 0) {
        return [];
      }
      // NOTE TIMELINEのLENGTH(=51)の分だけ初期データを生成する
      let newData: ApiResponse.StudentJobHuntingTrendData[] = Array(
        TIMELINE_LENGTH
      )
        .fill(0)
        .map((e, i) => this.generateInitialData(i))
        .sort((a, b) => {
          // 年->月->partの優先順位でソート
          // 年で並び替え
          if (a.year !== b.year) {
            return a.year - b.year;
          }
          // 月で並べ替え
          if (a.month !== b.month) {
            return a.month - b.month;
          }
          // partで並べ替え
          if (a.part !== b.part) {
            return a.part - b.part;
          }
          return 0;
        });

      // NOTE 対象のデータセットの卒年に合わせてStart日時を生成
      const startForThisDataSets = dayjs(`${graduateYear - 2}-04-01`);
      filteredData.forEach(e => {
        // NOTE 0埋めした月
        const formattedMonth =
          String(e.month).length === 1 ? `0${e.month}` : e.month;
        // NOTE 上中下旬を擬似的な日付に変換している
        const formattedBME =
          e.part % 3 === 0 ? "25" : e.part % 2 === 0 ? "15" : "05";
        // NOTE TimeLineの何本目に位置するか
        const dateFromData = dayjs(
          `${e.year}-${formattedMonth}-${formattedBME}`
        );
        const convertedDateToTimeLineIndex = Math.floor(
          dateFromData.diff(startForThisDataSets, "month", true) * 3
        );
        // NOTE 対象のタイムラインのデータを取得データで置き換える
        newData[convertedDateToTimeLineIndex].count = e.count;
      });
      return newData;
    },
    generateInitialData(index) {
      // NOTE 計算しやすいように1スタートにする
      const timeLineIndex = index + 1;
      // NOTE indexから年月partを逆算して返す
      return {
        graduate_year:
          timeLineIndex <= 9 * 3
            ? this.graduateYear - 2
            : this.graduateYear - 1,
        year:
          timeLineIndex <= 9 * 3
            ? this.graduateYear - 2
            : this.graduateYear - 1,
        month:
          Math.floor(index / 3 + 4) <= 12
            ? Math.floor(index / 3 + 4)
            : Math.floor(index / 3 + 4) - 12,
        part:
          timeLineIndex % 3 === 0
            ? BME_PART.END
            : timeLineIndex % 2 === 0
            ? BME_PART.MIDDLE
            : BME_PART.BEGINNING,
        count: 0
      };
    },
    // StudentSpeechBubbleなどと共通化
    getBMELabel(bme) {
      switch (bme) {
        case BME_PART.BEGINNING:
          return "上旬";
        case BME_PART.MIDDLE:
          return "中旬";
        case BME_PART.END:
          return "下旬";
      }
      return "";
    },
    onMousemoveChart(activePoint) {
      if (!activePoint) return;

      const { _datasetIndex, _index } = activePoint;

      if (
        this.lastMouseover._datasetIndex === _datasetIndex &&
        this.lastMouseover._index === _index
      )
        return;

      this.lastMouseover = { _datasetIndex, _index };
    }
  }
});
